import React, { useEffect, useRef } from "react";
import { MarkerClusterer } from "@react-google-maps/api";

// Custom renderer for the marker clusters
const createCustomClusterIcon = (cluster) => {
  const count = cluster.getSize();

  // Get the lowest price from all markers in this cluster
  let lowestPrice = Infinity;
  const markers = cluster.getMarkers();

  markers.forEach((marker) => {
    if (marker.hotelData && marker.hotelData.LowestPackagePrice) {
      lowestPrice = Math.min(lowestPrice, marker.hotelData.LowestPackagePrice);
    }
  });

  // Calculate dynamic size based on the number of points
  const minSize = 40;
  const maxSize = 60;
  const size = Math.min(minSize + Math.log2(count) * 5, maxSize);
  const radius = size / 2;

  // Create a custom SVG cluster icon
  const svg = window.btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
      <circle cx="${radius}" cy="${radius}" r="${radius - 4}" fill="#FF5722" stroke="white" stroke-width="2"/>
      <text x="${radius}" y="${radius - 3}" font-size="12" text-anchor="middle" fill="white" font-weight="bold">${count}</text>
      ${lowestPrice !== Infinity ? `<text x="${radius}" y="${radius + 9}" font-size="10" text-anchor="middle" fill="white">₪${Math.round(lowestPrice)}</text>` : ""}
    </svg>
  `);

  return {
    url: `data:image/svg+xml;base64,${svg}`,
    height: size,
    width: size,
    anchor: new window.google.maps.Point(radius, radius),
    textColor: "#FFFFFF",
  };
};

const HotelMarkerClusterer = ({ children, mapRef }) => {
  const clustererRef = useRef(null);

  // This effect ensures re-rendering of the clusterer when zoom changes
  useEffect(() => {
    if (mapRef && clustererRef.current) {
      const listener = mapRef.addListener("zoom_changed", () => {
        if (clustererRef.current) {
          clustererRef.current.repaint();
        }
      });

      return () => {
        window.google.maps.event.removeListener(listener);
      };
    }
  }, [mapRef]);

  // Custom algorithm to determine appropriate clustering
  const calculateClusters = (markers, numStyles) => {
    return {
      text: markers.length.toString(),
      index: Math.min(Math.floor(Math.log10(markers.length)) + 1, numStyles),
    };
  };

  // Custom renderer function
  const clusterRenderer = ({ count, position }, stats) => {
    const clusterIcon = createCustomClusterIcon({
      getSize: () => count,
      getMarkers: () => stats.markers,
      getPosition: () => position,
    });

    return new window.google.maps.Marker({
      position,
      icon: clusterIcon,
      label: null,
      zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
    });
  };

  // Dynamic grid size based on zoom level
  const getGridSize = () => {
    if (!mapRef) return 60;

    const zoom = mapRef.getZoom();
    // Adjust grid size based on zoom level
    // Higher zoom levels (closer view) use smaller grid size
    if (zoom >= 16) return 20;
    if (zoom >= 14) return 30;
    if (zoom >= 12) return 40;
    if (zoom >= 10) return 50;
    if (zoom >= 8) return 60;
    if (zoom >= 6) return 80;
    if (zoom >= 4) return 100;
    return 120; // Very wide grid for lowest zoom levels
  };

  return (
    <MarkerClusterer
      averageCenter
      enableRetinaIcons
      gridSize={getGridSize()}
      maxZoom={16} // Clusters will break apart at zoom level higher than 16
      minZoom={1} // Start clustering from the minimum zoom level
      calculator={calculateClusters}
      renderer={clusterRenderer}
      onLoad={(clusterer) => {
        clustererRef.current = clusterer;
      }}
    >
      {(clusterer) => {
        // Add hotel data to each marker for price information
        return React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              clusterer,
              onLoad: (marker) => {
                // Attach hotel data to the marker
                if (child.props.options && child.props.options.hotelData) {
                  marker.hotelData = child.props.options.hotelData;
                }
                // Call the original onLoad if it exists
                if (child.props.onLoad) {
                  child.props.onLoad(marker);
                }
              },
            });
          }
          return child;
        });
      }}
    </MarkerClusterer>
  );
};

export default HotelMarkerClusterer;
